/* eslint-disable no-confusing-arrow */
/* eslint-disable max-len */
import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    Box,
    Button,
    Divider,
    Grid,
    Tooltip,
    Typography
} from '@mui/material';
import {
    LocalShipping,
    Construction,
    CardMembership,
    Forest,
    ExpandLess,
    ExpandMore,
    Map as MapIcon
} from '@mui/icons-material';
import { ReactComponent as FairTransportEmblem } from '../../assets/icons/fairtransport-emblem.svg';
import {
    Block,
    Description,
    DriveLine,
    Fetch,
    Header,
    List,
    Numbers,
    SectionDivider
} from '../CommonComponents';
import { Container, Paper } from '../StyledComponents';
import { FetchComponentPropsType } from '../CommonComponents/Fetch/Fetch';
import OrderMap from '../Orders/OrderMap';
import { useDispatch, useSelector } from '../../redux/hooks';
import { selectTenantProfile, fetchTenantProfile } from '../../redux/actions/tenants';
import { getJob } from '../../api/jobs';
import reportClicks from '../../api/datadog';
import OrderStatus from '../Orders/OrderStatus';
import {
    isJobFullType,
    RequirementsMatchesType,
    type JobType,
    type OrderTypeType
} from '../../types';
import ChangeOrderContact from '../Orders/ChangeOrderContact';
import { getPublishedText } from '../../utils/order';
import checkValidUtmValues from '../../utils/datadog';
import OfferChat from '../Offers/OfferChat';
import OrderStatusAction from '../Orders/OrderStatusAction';
import Offer from '../Offers/Offer';
import DhlDetails from '../Market/EntityDetails/DhlDetails';
import { getTenantLinkForJobsOffers } from '../../utils/tenant-links';
import RequirementsSection from '../Orders/OrderRequirements';
import OrderFiles from '../Orders/OrderFiles';

const initialMatchingMetadata = {
    vehicleTypesMatch: [],
    equipmentMatch: [],
    sustainabilityMatch: [],
    licencesCertificatesMatch: [],
    customLicencesCertificatesMatch: [],
    fairtransportMatch: false
};

const JobDetailsView: React.FC<FetchComponentPropsType<JobType>> = (props) => {
    const { entity, refetch } = props;
    const dispatch = useDispatch();
    const tenantProfile = useSelector(selectTenantProfile());
    const navigate = useNavigate();
    const [metaDataCollapsed, setMetaDataCollapsed] = React.useState(false);
    const [showMap, setShowMap] = React.useState(false);
    const [matchingMetadata, setMatchingMetadata] = React.useState<RequirementsMatchesType>(initialMatchingMetadata);
    const [meetsAllRequirements, setMeetsAllRequirements] = React.useState<boolean>(true);

    const showChangeStatusButton = React.useMemo(() => (
        isJobFullType(entity) && ['ACCEPTED', 'STARTED'].some((s) => s === entity.status)
    ), [entity]);

    const handleNavigateToCompanyProfile = React.useCallback(() => {
        if (entity?.tenantId && !entity.isRejected && entity.isCarrier) {
            navigate(`/company-profile/${entity.tenantId}`);
        }
    }, [entity?.tenantId, entity?.isRejected, entity?.isCarrier, navigate]);

    const entityMetadataLength = React.useMemo(() => (
        entity ? [
            entity.vehicleTypes,
            entity.equipment,
            entity.sustainability,
            entity.licencesCertificates,
            entity.customLicencesCertificates
        ].reduce((sum, arr) => sum + (arr ? arr.length : 0), 0) : 0
    ), [entity]);

    React.useEffect(() => {
        if (!entity || !tenantProfile) return;
        if (entity.isMyJob) return;
        if (entityMetadataLength === 0) return;

        const mustBeFairTransport = (entity.sustainability || []).includes('Fair Transport');

        const entityVehicleTypes = entity.vehicleTypes || [];
        const entityEquipment = entity.equipment || [];
        const entitySustainabilityWithoutFairtransport = entity.sustainability.filter((sust: string) => sust !== 'Fair Transport') || [];
        const entityLicencesCertificates = entity.licencesCertificates || [];
        const entityCustomLicencesCertificates = entity.customLicencesCertificates || [];

        const tenantVehicleTypes = tenantProfile.vehicleTypes || [];
        const tenantEquipment = tenantProfile.equipment || [];
        const tenantSustainability = tenantProfile.sustainability || [];
        const tenantLicencesCertificates = tenantProfile.licencesCertificates || [];
        const tenantCustomLicencesCertificatesNormalized = (tenantProfile.customLicencesCertificates || []).map((lic) => lic.toLowerCase().trim());

        const vehicleTypesMatch = entityVehicleTypes.map((veh) => tenantVehicleTypes.includes(veh) ? { name: veh, has: true } : { name: veh, has: false });

        const equipmentMatch = entityEquipment.map((eq) => tenantEquipment.includes(eq) ? { name: eq, has: true } : { name: eq, has: false });

        const sustainabilityMatch = entitySustainabilityWithoutFairtransport.map((sus) => tenantSustainability.includes(sus) ? { name: sus, has: true } : { name: sus, has: false });

        const licencesCertificatesMatch = entityLicencesCertificates.map((lic) => tenantLicencesCertificates.includes(lic) ? { name: lic, has: true } : { name: lic, has: false });

        const customLicencesCertificatesMatch = entityCustomLicencesCertificates.map((lic) => tenantCustomLicencesCertificatesNormalized.includes(lic.toLowerCase().trim()) ? { name: lic, has: true } : { name: lic, has: false });

        const allRequirementsMet = [...vehicleTypesMatch, ...equipmentMatch, ...sustainabilityMatch, ...licencesCertificatesMatch, ...customLicencesCertificatesMatch].every((match) => match.has) && (mustBeFairTransport ? tenantProfile.isFairtransportMember : true);

        setMeetsAllRequirements(allRequirementsMet);

        setMatchingMetadata({
            vehicleTypesMatch,
            equipmentMatch,
            sustainabilityMatch,
            licencesCertificatesMatch,
            customLicencesCertificatesMatch,
            fairtransportMatch: mustBeFairTransport ? tenantProfile.isFairtransportMember : true
        });
    }, [entity, tenantProfile, entityMetadataLength]);

    React.useEffect(() => {
        dispatch(fetchTenantProfile());
    }, [dispatch]);

    const entitySustainabilityWithoutFairtransport = React.useMemo(() => {
        if (!entity) return [];
        return (entity.sustainability || []).filter((s: string) => s !== 'Fair Transport');
    }, [entity]);

    const mustBeFairtransport = React.useMemo(() => {
        if (!entity) return false;
        return (entity.sustainability || []).includes('Fair Transport');
    }, [entity]);

    return !entity ? null : (
        <Container>
            <Header
                title="Kör uppdrag"
                CustomComponent={(
                    <>
                        <Button
                            sx={{ ml: 1 }}
                            variant="outlined"
                            startIcon={<MapIcon />}
                            onClick={() => setShowMap((visible) => !visible)}
                        >
                            {showMap ? 'Dölj karta' : 'Visa karta'}
                        </Button>
                        {showChangeStatusButton && (
                            <OrderStatusAction data={entity} refetch={refetch} />
                        )}
                    </>
                )}
            />

            <Grid container spacing={2}>
                {showMap && (
                    <Grid item xs={12}>
                        <Block>
                            <OrderMap
                                entityType={entity.type}
                                approximate={!isJobFullType(entity)}
                                pickupLat={entity.pickupLat}
                                pickupLng={entity.pickupLng}
                                deliveryLat={entity.deliveryLat}
                                deliveryLng={entity.deliveryLng}
                            />
                        </Block>
                    </Grid>
                )}

                {isJobFullType(entity) && ['ACCEPTED', 'STARTED', 'COMPLETED'].some((s) => s === entity.status) && (
                    <Grid item xs={12}>
                        <OrderStatus data={entity} />
                    </Grid>
                )}

                <Grid item xs={12} lg={8}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Paper>
                                <DriveLine data={entity} />
                                <Numbers
                                    small
                                    data={[
                                        { title: 'Totalvikt [kg]', value: entity.grossWeight },
                                        { title: 'FDR-vikt [kg]', value: entity.chargeableWeight },
                                        { title: 'Volym [m³]', value: entity.volume },
                                        { title: 'Flakmeter', value: entity.loadingMeters },
                                        { title: 'Pallplatser', value: entity.palletPlaces },
                                        { title: 'Pallar', value: entity.pallets }
                                    ]}
                                />
                                {entityMetadataLength > 0 && (
                                    <>
                                        <SectionDivider
                                            label={(
                                                <>
                                                    {`krav (${entityMetadataLength})`}
                                                    {metaDataCollapsed ? <ExpandMore /> : <ExpandLess />}
                                                </>
                                            )}
                                            color={metaDataCollapsed && !meetsAllRequirements ? 'error' : 'black'}
                                            marginTop={1}
                                            marginBottom={0}
                                            onClick={() => setMetaDataCollapsed(!metaDataCollapsed)}
                                        />
                                        {!metaDataCollapsed && (
                                            <>
                                                {mustBeFairtransport && (
                                                    <Grid item xs={12} p={2} alignItems="center">
                                                        <Box sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            columnGap: 1
                                                        }}
                                                        >
                                                            <Tooltip
                                                                arrow
                                                                title={!matchingMetadata.fairtransportMatch
                                                                    ? 'Ditt företag är inte certifierat av Fair Transport enligt våra uppgifter. Stämmer inte detta - kontakta vår support.'
                                                                    : ''}
                                                            >
                                                                <FairTransportEmblem style={{ height: 25, width: 25 }} color={matchingMetadata.fairtransportMatch ? 'success' : 'error'} />
                                                            </Tooltip>
                                                            <Typography variant="body2" component="span" sx={{ color: matchingMetadata.fairtransportMatch ? 'success.main' : 'error.main', fontWeight: 300 }}>
                                                                Kräver certifiering av Fair Transport
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                )}
                                                <Grid item xs={12}>
                                                    <Grid
                                                        container
                                                        rowSpacing={1}
                                                        columnSpacing={0}
                                                        alignItems="flex-start"
                                                    >
                                                        {(entity.vehicleTypes || []).length > 0 && (
                                                            <RequirementsSection
                                                                label="Fordonstyper"
                                                                entityMetadata={entity.vehicleTypes || []}
                                                                requirementsMatches={matchingMetadata.vehicleTypesMatch}
                                                                icon={<LocalShipping fontSize="small" />}
                                                                isMyJob={false}
                                                            />
                                                        )}
                                                        {(entity.equipment || []).length > 0 && (
                                                            <RequirementsSection
                                                                label="Utrustning"
                                                                entityMetadata={entity.equipment || []}
                                                                requirementsMatches={matchingMetadata.equipmentMatch}
                                                                icon={<Construction fontSize="small" />}
                                                                isMyJob={false}
                                                            />
                                                        )}
                                                        {entitySustainabilityWithoutFairtransport.length > 0 && (
                                                            <RequirementsSection
                                                                label="Hållbarhet"
                                                                entityMetadata={entitySustainabilityWithoutFairtransport}
                                                                requirementsMatches={matchingMetadata.sustainabilityMatch}
                                                                icon={<Forest fontSize="small" />}
                                                                isMyJob={false}
                                                            />
                                                        )}
                                                        {[
                                                            ...(entity.licencesCertificates || []),
                                                            ...(entity.customLicencesCertificates || [])
                                                        ].length > 0 && (
                                                            <RequirementsSection
                                                                label="Licenser & certifikat"
                                                                entityMetadata={[
                                                                    ...(entity.licencesCertificates || []),
                                                                    ...(entity.customLicencesCertificates || [])
                                                                ]}
                                                                requirementsMatches={[
                                                                    ...matchingMetadata.licencesCertificatesMatch,
                                                                    ...matchingMetadata.customLicencesCertificatesMatch
                                                                ]}
                                                                icon={<CardMembership fontSize="small" />}
                                                                isMyJob={false}
                                                            />
                                                        )}
                                                    </Grid>
                                                </Grid>
                                            </>
                                        )}
                                    </>
                                )}
                                <Grid container spacing={2} style={{ marginTop: 10 }}>
                                    {entity.publicDescription && (
                                        <Grid item xs={12} lg>
                                            <Description
                                                title="Beskrivning"
                                                text={entity.publicDescription}
                                            />
                                        </Grid>
                                    )}
                                    {isJobFullType(entity) && entity.privateDescription && (
                                        <Grid item xs={12} lg>
                                            <Description
                                                title="Privat beskrivning"
                                                text={entity.privateDescription}
                                            />
                                        </Grid>
                                    )}

                                    <Grid item xs={12}>
                                        <Divider />
                                        {getPublishedText({ entity, handleNavigateToCompanyProfile })}
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>

                        {isJobFullType(entity) && ['ACCEPTED', 'STARTED', 'COMPLETED'].some((s) => s === entity.status) && (
                            <>
                                <Grid item xs={12} xl={6}>
                                    {isJobFullType(entity) && ['ACCEPTED', 'STARTED', 'COMPLETED'].some((s) => s === entity.status) && (
                                        <Block
                                            title="Kontaktuppgifter"
                                            ActionComponent={entity ? (
                                                <ChangeOrderContact
                                                    orderId={entity.id}
                                                    contactId={entity.acceptedTenantContactId}
                                                    contactName={entity.acceptedTenantContactName}
                                                    contactType="carrier-contact"
                                                    refetch={refetch}
                                                />
                                            ) : undefined}
                                        >
                                            <List
                                                data={[
                                                    {
                                                        name: 'Företag',
                                                        value: getTenantLinkForJobsOffers(entity)
                                                    },
                                                    {
                                                        name: 'Företag, e-post',
                                                        value: entity.tenantEmail,
                                                        type: 'email'
                                                    },
                                                    {
                                                        name: 'Företag, telefon',
                                                        value: entity.tenantPhone,
                                                        type: 'phone'
                                                    },
                                                    {
                                                        name: 'Kontaktperson',
                                                        value: entity.tenantContactName
                                                    },
                                                    {
                                                        name: 'Kontaktperson, e-post',
                                                        value: entity.tenantContactEmail,
                                                        type: 'email'
                                                    },
                                                    {
                                                        name: 'Kontaktperson, mobil',
                                                        value: entity.tenantContactPhone,
                                                        type: 'phone'
                                                    },
                                                    {
                                                        name: 'Kontaktperson, telefon',
                                                        value: entity.tenantContactPhoneAlt,
                                                        type: 'phone'
                                                    }
                                                ]}
                                            />
                                        </Block>
                                    )}
                                </Grid>

                                <Grid item xs={12} xl={6}>
                                    <Block title="Faktureringsuppgifter">
                                        <List
                                            data={[
                                                {
                                                    name: 'Företag',
                                                    value: getTenantLinkForJobsOffers(entity)
                                                },
                                                {
                                                    name: 'Märkning',
                                                    value: entity.tenantBillingMark
                                                },
                                                {
                                                    name: 'Gata',
                                                    value: entity.tenantBillingStreet
                                                },
                                                {
                                                    name: 'Postnummer',
                                                    value: entity.tenantBillingZip
                                                },
                                                {
                                                    name: 'Postort',
                                                    value: entity.tenantBillingCity
                                                },
                                                {
                                                    name: 'E-post',
                                                    value: entity.tenantBillingEmail,
                                                    type: 'email'
                                                }
                                            ]}
                                        />
                                    </Block>
                                </Grid>
                            </>
                        )}
                    </Grid>
                </Grid>

                <Grid item xs={12} lg={4}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            {entity.type === 'LL' && (
                                <Paper>
                                    <Offer
                                        order={entity}
                                        offer={entity.offer}
                                        refetch={refetch}
                                    />
                                </Paper>
                            )}
                            {entity.type === 'DHL' && (
                                <Paper>
                                    <DhlDetails entity={entity} />
                                </Paper>
                            )}
                        </Grid>
                        {entity.type === 'LL' && (
                            <Grid item xs={12}>
                                <Block title="Meddelanden" contentPadding>
                                    <OfferChat
                                        recipient={entity.tenantName || '[Dold uppdragsgivare]'}
                                        id={entity.offer?.id}
                                        orderId={entity.id}
                                        canWrite={entity.isOpen}
                                        maxHeight="705px"
                                    />
                                </Block>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
                {isJobFullType(entity) && entity.type === 'LL' && ['ACCEPTED', 'STARTED', 'COMPLETED'].some((s) => s === entity.status) && (
                    <Grid item xs={12}>
                        <OrderFiles orderId={entity.id} />
                    </Grid>
                )}
            </Grid>
        </Container>
    );
};

export default function JobDetails() {
    const { type, id } = useParams() as unknown as { type: string, id: number };
    const [searchParams] = useSearchParams();

    const fetchJob = React.useCallback(() => getJob({ type: type as OrderTypeType, id }), [id, type]);

    const {
        hasValidUtmValues,
        utmSource,
        utmMedium,
        utmType
    } = checkValidUtmValues(searchParams);

    React.useEffect(() => {
        if (hasValidUtmValues && utmSource && utmMedium && utmType) {
            reportClicks({
                utmSource,
                utmMedium,
                utmType
            });
        }
    }, [hasValidUtmValues, utmSource, utmMedium, utmType]);

    return (
        <Fetch<JobType>
            fetch={fetchJob}
            Component={JobDetailsView}
            name="uppdraget"
            notFoundDescription="Det har antingen tagits bort eller förmedlats till någon annan."
        />
    );
}
